import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CollaborationState, CollaborationDocument, CollaborationSession } from './collaborationTypes';
import { collaborationAPI } from './collaborationAPI';

const initialState: CollaborationState = {
  documents: [],
  currentDocument: null,
  activeSessions: [],
  loading: false,
  error: null,
};

const collaborationSlice = createSlice({
  name: 'collaboration',
  initialState,
  reducers: {
    setCurrentDocument: (state, action: PayloadAction<CollaborationDocument | null>) => {
      state.currentDocument = action.payload;
    },
    
    clearCollaborationError: (state) => {
      state.error = null;
    },
    
    addActiveSession: (state, action: PayloadAction<CollaborationSession>) => {
      const existingIndex = state.activeSessions.findIndex(
        s => s.document_id === action.payload.document_id
      );
      if (existingIndex >= 0) {
        state.activeSessions[existingIndex] = action.payload;
      } else {
        state.activeSessions.push(action.payload);
      }
    },
    
    removeActiveSession: (state, action: PayloadAction<string>) => {
      state.activeSessions = state.activeSessions.filter(
        s => s.document_id !== action.payload
      );
    },
    
    updateDocumentContent: (state, action: PayloadAction<{ document_id: string; content: string }>) => {
      if (state.currentDocument && state.currentDocument._id === action.payload.document_id) {
        state.currentDocument.content = action.payload.content;
      }
      const doc = state.documents.find(d => d._id === action.payload.document_id);
      if (doc) {
        doc.content = action.payload.content;
      }
    },
  },
  
  extraReducers: (builder) => {
    // Get Documents
    builder.addMatcher(
      collaborationAPI.endpoints.getDocuments.matchPending,
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );
    builder.addMatcher(
      collaborationAPI.endpoints.getDocuments.matchFulfilled,
      (state, action) => {
        state.loading = false;
        state.documents = action.payload;
      }
    );
    builder.addMatcher(
      collaborationAPI.endpoints.getDocuments.matchRejected,
      (state, action: any) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to fetch documents';
      }
    );
    
    // Get Document
    builder.addMatcher(
      collaborationAPI.endpoints.getDocument.matchFulfilled,
      (state, action) => {
        state.currentDocument = action.payload;
      }
    );
    
    // Create Document
    builder.addMatcher(
      collaborationAPI.endpoints.createDocument.matchFulfilled,
      (state, action) => {
        state.documents.unshift(action.payload);
        state.currentDocument = action.payload;
      }
    );
    
    // Get Active Sessions
    builder.addMatcher(
      collaborationAPI.endpoints.getActiveSessions.matchFulfilled,
      (state, action) => {
        state.activeSessions = action.payload;
      }
    );
  },
});

export const {
  setCurrentDocument,
  clearCollaborationError,
  addActiveSession,
  removeActiveSession,
  updateDocumentContent,
} = collaborationSlice.actions;

export default collaborationSlice.reducer;
